import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Brain, HelpCircle, Lightbulb, Target, TrendingUp, 
  Users, BarChart3, PieChart, Activity, Zap,
  BookOpen, MessageSquare, RefreshCw, CheckCircle,
  AlertTriangle, ArrowUp, ArrowDown, Minus, User,
  ChevronLeft, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function AIInteractionInsights({ students = [], teacherCourses = [] }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentsList, setStudentsList] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentInsights, setStudentInsights] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [viewMode, setViewMode] = useState('class'); // 'class' or 'student'
  const token = localStorage.getItem('faheem_token');

  // Fetch class-wide analytics
  useEffect(() => {
    const fetchClassAnalytics = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const [analyticsRes, studentsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/ai-analytics/class-stats`, {
            params: { access_token: token }
          }),
          axios.get(`${BACKEND_URL}/api/ai-analytics/students-list`, {
            params: { access_token: token }
          })
        ]);
        
        setInsights(analyticsRes.data);
        setStudentsList(studentsRes.data.students || []);
      } catch (error) {
        console.error('Error fetching AI analytics:', error);
        toast.error('Failed to load AI analytics');
        // Set fallback data
        setInsights(getFallbackData());
      } finally {
        setLoading(false);
      }
    };
    
    fetchClassAnalytics();
  }, [token]);

  // Fetch individual student analytics
  const fetchStudentAnalytics = async (studentId) => {
    if (!token || !studentId) return;
    
    setLoadingStudent(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-analytics/student-stats/${studentId}`, {
        params: { access_token: token }
      });
      setStudentInsights(response.data);
      setViewMode('student');
    } catch (error) {
      console.error('Error fetching student analytics:', error);
      toast.error('Failed to load student analytics');
    } finally {
      setLoadingStudent(false);
    }
  };

  const getFallbackData = () => ({
    helpSeekingBehavior: { directAnswers: 18, conceptualClarification: 42, guidedThinking: 28, regeneratingAnswers: 12 },
    conceptualPercent: 65,
    solutionPercent: 35,
    dependencyScore: 32,
    dependencyLevel: "Low",
    dependencyColor: "green",
    attemptBeforeAskingPercent: 68,
    engagementDepth: { surface: 22, moderate: 45, deep: 33 },
    weeklyTrend: 8,
    totalInteractions: 0,
    activeStudents: students.length || 0,
    avgInteractions: 0,
    peakTimes: [
      { time: "Morning (8-12)", percent: 22 },
      { time: "Afternoon (12-5)", percent: 28 },
      { time: "Evening (5-9)", percent: 35 },
      { time: "Night (9-12)", percent: 15 }
    ],
    isRealData: false,
    note: "Sample data shown. Real analytics will appear as students interact with the AI tutor."
  });

  // Student Individual View Component
  const StudentInsightsView = () => {
    if (!studentInsights) return null;
    
    const data = studentInsights;
    
    return (
      <div className="space-y-6">
        {/* Back Button and Student Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              setViewMode('class');
              setSelectedStudent(null);
              setStudentInsights(null);
            }}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Class View
          </Button>
        </div>
        
        <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 border-2 border-white/20">
                <AvatarImage src={selectedStudent?.avatar_url} />
                <AvatarFallback className="bg-white/20 text-white text-xl">
                  {data.student?.username?.charAt(0)?.toUpperCase() || 'S'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl font-bold">{data.student?.username || 'Student'}</h3>
                <p className="text-slate-300">{data.student?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-white/20 text-white">
                    {data.totalInteractions} Total Interactions
                  </Badge>
                  {data.isRealData && (
                    <Badge className="bg-green-500/30 text-green-200 border border-green-400/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Real Data
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            {data.isRealData && data.totalInteractions > 0 && (
              <div className="mt-4 p-3 bg-green-500/20 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-300" />
                <span className="text-sm text-green-100">Showing real analytics based on {data.totalInteractions} actual AI tutor interactions</span>
              </div>
            )}
            
            {!data.isRealData && (
              <div className="mt-4 p-3 bg-amber-500/20 rounded-lg flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-300" />
                <span className="text-sm text-amber-100">{data.note || "Sample data - real analytics will appear when this student uses the AI tutor"}</span>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Student Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Help-Seeking Behavior */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                Help-Seeking Pattern
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Direct Answers</span>
                  <span className="font-semibold">{data.helpSeekingBehavior?.directAnswers || 0}%</span>
                </div>
                <Progress value={data.helpSeekingBehavior?.directAnswers || 0} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Conceptual Questions</span>
                  <span className="font-semibold">{data.helpSeekingBehavior?.conceptualClarification || 0}%</span>
                </div>
                <Progress value={data.helpSeekingBehavior?.conceptualClarification || 0} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Guided Thinking</span>
                  <span className="font-semibold">{data.helpSeekingBehavior?.guidedThinking || 0}%</span>
                </div>
                <Progress value={data.helpSeekingBehavior?.guidedThinking || 0} className="h-2" />
              </div>
            </CardContent>
          </Card>
          
          {/* Engagement Depth */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-indigo-600" />
                Engagement Depth
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-600">{data.engagementDepth?.surface || 0}%</p>
                  <p className="text-xs text-slate-500">Surface</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{data.engagementDepth?.moderate || 0}%</p>
                  <p className="text-xs text-slate-500">Moderate</p>
                </div>
                <div className="text-center p-3 bg-indigo-50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600">{data.engagementDepth?.deep || 0}%</p>
                  <p className="text-xs text-slate-500">Deep</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Self-Attempt Rate */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                Self-Attempt Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-3">
                  <span className="text-3xl font-bold text-green-600">{data.attemptBeforeAskingPercent || 0}%</span>
                </div>
                <p className="text-sm text-slate-600">
                  {data.attemptBeforeAskingPercent >= 60 
                    ? "This student often tries problems independently first!" 
                    : "Encourage this student to attempt problems before asking for help."}
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Follow-up Questions */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                Follow-up Behavior
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-purple-100 mb-3">
                  <span className="text-3xl font-bold text-purple-600">{data.avgFollowUps || 0}</span>
                </div>
                <p className="text-sm text-slate-600">Average follow-up questions per session</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading AI interaction insights...</p>
        </div>
      </div>
    );
  }

  // Show student view if selected
  if (viewMode === 'student' && studentInsights) {
    return <StudentInsightsView />;
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "slate", trend = null }) => (
    <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold text-slate-900">{value}</p>
              {trend !== null && (
                <span className={`flex items-center text-xs font-medium ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                  {trend > 0 ? <ArrowUp className="w-3 h-3" /> : trend < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                  {Math.abs(trend)}%
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI Interaction Insights</h2>
              <p className="text-slate-300">Anonymized behavioral analytics from FaheemGPT Tutor</p>
            </div>
          </div>
          
          {/* Student Selector */}
          {studentsList.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-300">View student:</span>
              <Select 
                value={selectedStudent?.id || ''} 
                onValueChange={(value) => {
                  const student = studentsList.find(s => s.id === value);
                  setSelectedStudent(student);
                  if (student) {
                    fetchStudentAnalytics(student.id);
                  }
                }}
              >
                <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select student" />
                </SelectTrigger>
                <SelectContent>
                  {studentsList.map(student => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.username || student.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        
        <div className="mt-4 p-3 bg-white/10 rounded-lg backdrop-blur-sm">
          <p className="text-sm text-slate-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span>Privacy Notice: This dashboard shows only aggregated, anonymized patterns. No individual conversations or messages are displayed.</span>
          </p>
        </div>
        
        {/* Show real data indicator */}
        {insights && insights.isRealData && insights.totalInteractions > 0 && (
          <div className="mt-3 p-3 bg-green-500/20 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-300" />
            <span className="text-sm text-green-100">Showing real analytics based on {insights.totalInteractions} actual AI tutor interactions from your students</span>
          </div>
        )}
        
        {/* Show note if using sample data */}
        {insights && !insights.isRealData && insights.note && (
          <div className="mt-3 p-3 bg-amber-500/20 rounded-lg flex items-center gap-2">
            <Info className="w-4 h-4 text-amber-300" />
            <span className="text-sm text-amber-100">{insights.note}</span>
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <Users className="w-5 h-5 mx-auto mb-1 text-blue-400" />
            <p className="text-2xl font-bold">{insights.activeStudents}</p>
            <p className="text-xs text-slate-300">Active Users</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <MessageSquare className="w-5 h-5 mx-auto mb-1 text-purple-400" />
            <p className="text-2xl font-bold">{insights.totalInteractions}</p>
            <p className="text-xs text-slate-300">Weekly Interactions</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <Activity className="w-5 h-5 mx-auto mb-1 text-green-400" />
            <p className="text-2xl font-bold">{insights.avgInteractions}</p>
            <p className="text-xs text-slate-300">Avg per Student</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-amber-400" />
            <p className="text-2xl font-bold">{insights.weeklyTrend > 0 ? '+' : ''}{insights.weeklyTrend}%</p>
            <p className="text-xs text-slate-300">Weekly Trend</p>
          </div>
        </div>
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Help-Seeking Behavior Distribution */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-600" />
              Help-Seeking Behavior Distribution
            </CardTitle>
            <CardDescription>How students approach the AI tutor for assistance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    Direct Answer Requests
                  </span>
                  <span className="font-semibold text-slate-900">{insights.helpSeekingBehavior.directAnswers}%</span>
                </div>
                <Progress value={insights.helpSeekingBehavior.directAnswers} className="h-2 bg-slate-100" />
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    Conceptual Clarification
                  </span>
                  <span className="font-semibold text-slate-900">{insights.helpSeekingBehavior.conceptualClarification}%</span>
                </div>
                <Progress value={insights.helpSeekingBehavior.conceptualClarification} className="h-2 bg-slate-100" />
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    Guided Thinking Mode
                  </span>
                  <span className="font-semibold text-slate-900">{insights.helpSeekingBehavior.guidedThinking}%</span>
                </div>
                <Progress value={insights.helpSeekingBehavior.guidedThinking} className="h-2 bg-slate-100" />
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    Regenerating Answers
                  </span>
                  <span className="font-semibold text-slate-900">{insights.helpSeekingBehavior.regeneratingAnswers}%</span>
                </div>
                <Progress value={insights.helpSeekingBehavior.regeneratingAnswers} className="h-2 bg-slate-100" />
              </div>
            </div>
            
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">
                <Lightbulb className="w-3 h-3 inline mr-1" />
                Higher conceptual clarification indicates deeper learning engagement
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Conceptual vs Solution-Oriented */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-600" />
              Conceptual vs Solution-Oriented
            </CardTitle>
            <CardDescription>Balance between understanding concepts and getting answers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-4">
              <div className="relative w-48 h-48">
                {/* Donut chart simulation */}
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="20"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="20"
                    strokeDasharray={`${insights.conceptualPercent * 2.51} 251`}
                    className="transition-all duration-1000"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="20"
                    strokeDasharray={`${insights.solutionPercent * 2.51} 251`}
                    strokeDashoffset={`-${insights.conceptualPercent * 2.51}`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-slate-900">{insights.conceptualPercent}%</p>
                    <p className="text-xs text-slate-500">Conceptual</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-slate-600">Conceptual ({insights.conceptualPercent}%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-sm text-slate-600">Solution ({insights.solutionPercent}%)</span>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                {insights.conceptualPercent >= 60 
                  ? "Healthy balance! Students are focusing on understanding concepts."
                  : "Consider encouraging more conceptual exploration over direct answers."}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* AI Dependency Index */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-600" />
              AI Dependency Index
            </CardTitle>
            <CardDescription>Class-level reliance on direct AI answers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full bg-${insights.dependencyColor}-100 mb-4`}>
                <div className="text-center">
                  <p className={`text-4xl font-bold text-${insights.dependencyColor}-600`}>{insights.dependencyScore}</p>
                  <p className="text-xs text-slate-500">/100</p>
                </div>
              </div>
              
              <Badge className={`text-lg px-4 py-1 bg-${insights.dependencyColor}-100 text-${insights.dependencyColor}-700 border-${insights.dependencyColor}-200`}>
                {insights.dependencyLevel} Dependency
              </Badge>
              
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className={`p-2 rounded-lg ${insights.dependencyLevel === 'Low' ? 'bg-green-100 ring-2 ring-green-500' : 'bg-slate-50'}`}>
                  <p className="text-xs font-medium text-slate-600">Low</p>
                  <p className="text-xs text-slate-400">0-35</p>
                </div>
                <div className={`p-2 rounded-lg ${insights.dependencyLevel === 'Moderate' ? 'bg-amber-100 ring-2 ring-amber-500' : 'bg-slate-50'}`}>
                  <p className="text-xs font-medium text-slate-600">Moderate</p>
                  <p className="text-xs text-slate-400">36-60</p>
                </div>
                <div className={`p-2 rounded-lg ${insights.dependencyLevel === 'High' ? 'bg-red-100 ring-2 ring-red-500' : 'bg-slate-50'}`}>
                  <p className="text-xs font-medium text-slate-600">High</p>
                  <p className="text-xs text-slate-400">61-100</p>
                </div>
              </div>
            </div>
            
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">
                {insights.dependencyLevel === 'Low' && "Excellent! Students are using AI as a learning aid, not a crutch."}
                {insights.dependencyLevel === 'Moderate' && "Balanced usage. Monitor for any increases in direct answer requests."}
                {insights.dependencyLevel === 'High' && "Consider promoting more guided thinking approaches in class."}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Attempt Before Asking */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-green-600" />
              Attempt Before Asking Metric
            </CardTitle>
            <CardDescription>Students who try solving before requesting help</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="py-6">
              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-600">Self-Attempt Rate</span>
                  <span className="text-2xl font-bold text-slate-900">{insights.attemptBeforeAskingPercent}%</span>
                </div>
                <div className="overflow-hidden h-4 text-xs flex rounded-full bg-slate-100">
                  <div
                    style={{ width: `${insights.attemptBeforeAskingPercent}%` }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-1000"
                  />
                </div>
              </div>
              
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{insights.attemptBeforeAskingPercent}%</p>
                  <p className="text-xs text-green-700">Try First</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-600">{100 - insights.attemptBeforeAskingPercent}%</p>
                  <p className="text-xs text-slate-500">Ask Directly</p>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  {insights.attemptBeforeAskingPercent >= 60 
                    ? "Great! Most students attempt problems independently first."
                    : "Encourage students to try solving problems before asking for help."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Engagement Depth Indicator - Full Width */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" />
            Engagement Depth Indicator
          </CardTitle>
          <CardDescription>Quality of student interactions with the AI tutor</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4">
            {/* Surface Level */}
            <div className="text-center p-4 rounded-xl bg-slate-50 border-2 border-slate-200">
              <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-slate-600">{insights.engagementDepth.surface}%</span>
              </div>
              <h4 className="font-semibold text-slate-900 mb-1">Surface-Level</h4>
              <p className="text-xs text-slate-500">Quick questions, minimal follow-up</p>
              <div className="mt-3">
                <Progress value={insights.engagementDepth.surface} className="h-2 bg-slate-200" />
              </div>
            </div>
            
            {/* Moderate */}
            <div className="text-center p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
              <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-blue-600">{insights.engagementDepth.moderate}%</span>
              </div>
              <h4 className="font-semibold text-slate-900 mb-1">Moderate Engagement</h4>
              <p className="text-xs text-slate-500">Follow-up questions, some exploration</p>
              <div className="mt-3">
                <Progress value={insights.engagementDepth.moderate} className="h-2 bg-blue-200" />
              </div>
            </div>
            
            {/* Deep */}
            <div className="text-center p-4 rounded-xl bg-indigo-50 border-2 border-indigo-200">
              <div className="w-16 h-16 bg-indigo-200 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-indigo-600">{insights.engagementDepth.deep}%</span>
              </div>
              <h4 className="font-semibold text-slate-900 mb-1">Deep Inquiry</h4>
              <p className="text-xs text-slate-500">Extended dialogue, concept exploration</p>
              <div className="mt-3">
                <Progress value={insights.engagementDepth.deep} className="h-2 bg-indigo-200" />
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-indigo-900 mb-1">Insight</h4>
                <p className="text-sm text-indigo-700">
                  {insights.engagementDepth.deep >= 25 
                    ? "Strong deep inquiry behavior indicates students are actively exploring concepts and building understanding."
                    : "Consider encouraging students to ask follow-up questions and explore topics more deeply with the AI tutor."}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Peak Usage Times */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-slate-600" />
            Peak Usage Times
          </CardTitle>
          <CardDescription>When students are most active with the AI tutor</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {insights.peakTimes.map((time, idx) => (
              <div key={idx} className="text-center">
                <div className="relative h-32 bg-slate-100 rounded-lg overflow-hidden mb-2">
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-700 to-slate-500 transition-all duration-500"
                    style={{ height: `${time.percent}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-white drop-shadow">{time.percent}%</span>
                  </div>
                </div>
                <p className="text-xs text-slate-600 font-medium">{time.time}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Individual Students List */}
      {studentsList.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-600" />
              Student AI Interaction Analytics
            </CardTitle>
            <CardDescription>Click on a student to view their individual AI interaction patterns</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {studentsList.map(student => (
                <div 
                  key={student.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-slate-300 cursor-pointer transition-all"
                  onClick={() => {
                    setSelectedStudent(student);
                    fetchStudentAnalytics(student.id);
                  }}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={student.avatar_url} />
                    <AvatarFallback className="bg-slate-200 text-slate-600">
                      {student.username?.charAt(0)?.toUpperCase() || 'S'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{student.username || 'Unknown'}</p>
                    <p className="text-xs text-slate-500 truncate">{student.course_names?.join(', ') || 'No courses'}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                    <ChevronLeft className="w-4 h-4 rotate-180" />
                  </Button>
                </div>
              ))}
            </div>
            
            {loadingStudent && (
              <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Loading student analytics...</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
